import { Typography } from '@mui/material';
import moment from "moment";
import {
  AutocompleteInput,
  Datagrid,
  DateField,
  Filter,
  FunctionField,
  List,
  NumberField,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceInput,
  SelectArrayInput,
  TextField,
  TopToolbar,
  useListContext,
  usePermissions,
  useTranslate,
} from "react-admin";
import { CheckChainUsesModule, CustomExportButton, CustomizableDatagrid, ModuleKey, Permission } from "../../components";
import { BetweenDatesInput } from "../../components/BetweenDatesInput";
import { PERMISSIONS } from "../../constants";
import { getChainId, getEmployeeType } from "../../lib";
import { EmployeeType, FillinOrigin, TagType } from "../../models";
import { formatAttendantLabel, filterToQuery } from '../../utils';

const titleSx = {
  margin: '8px 0 8px 0',
  textAlign: 'center',
  fontWeight: 'bold'
};

const refArrayInputSx = {
  minWidth: 160,
  '& .MuiFormHelperText-marginDense': {
    height: 0
  }
};

const ListActions = () => {
  const preExporter = async (data, fetchRelatedRecords) => {
    const fillins = data[0].data;
    const relations = [
      { field: 'attendantId', resource: 'attendants' },
      { field: 'placeId', resource: 'stations' },
      { field: 'id', resource: 'customers' },
    ];
    const fetchData = await fetchRelatedRecords(relations, fillins);

    const getData = (resource) => {
      return fetchData.find(({ resource: res }) => res === resource)?.results || [];
    }

    fillins.forEach(fillin => {
      const { attendantId, placeId, id: customerId } = fillin;
      const attendant = getData('attendants').find(({ id }) => id === attendantId);
      const place = getData('stations').find(({ id }) => id === placeId);
      const customer = getData('customers').find(({ id }) => id === customerId);

      fillin.attendant_name = attendant?.name || 'Não identificado';
      fillin.place_name = place?.name || 'Não identificado';
      fillin.customer_name = customer?.name || 'Não identificado';
    });

    return fillins;
  };

  return (
    <TopToolbar>
      <CustomExportButton preExport={preExporter} />
    </TopToolbar>
  );
};

const FirstFillinByAttendantList = () => {
  const props = useListContext();

  let attendantSummaryData = [], totalCustomersSummaryData = [];
  const data = [];

  if (props.data) {
    const reportInfo = props.data[0];

    if (reportInfo) {
      reportInfo.data.forEach(({ id, ...item }) => {
        data.push({ ...item, customerId: id });
      });
      attendantSummaryData = reportInfo.summaryData.attendantSummaryData;
      totalCustomersSummaryData = reportInfo.summaryData.totalCustomersSummaryData;
    }
  }

  return (
    <>
      <Typography sx={titleSx}>Primeiro Abastecimento por Frentista</Typography>
      <Datagrid bulkActionButtons={false} data={totalCustomersSummaryData} style={{ marginBottom: 60 }} total={1}>
        <NumberField
          source="totalCustomers"
          label="Total de Clientes"
          textAlign="center"
          emptyText="Não definido"
          sortable={false}
        />
      </Datagrid>
      <Typography sx={titleSx}>Frentistas</Typography>
      <Datagrid bulkActionButtons={false} data={attendantSummaryData} style={{ marginBottom: 50 }} total={1}>
        <ReferenceField source="attendant_id" reference="attendants" label="Frentista" basePath="attendants" sortable={false} emptyText="Não identificado">
          <FunctionField render={record => record && (record.name || record.identfid) ? record.name || record.identfid : 'Não identificado'} label='Nome/Identfid' emptyText="Não identificado" />
        </ReferenceField>
        <NumberField textAlign="center" source="count" label="Total de Clientes" sortable={false} />
      </Datagrid>

      <Typography sx={titleSx}>Primeiro Abastecimento de Clientes</Typography>
      <Datagrid bulkActionButtons={false} resource={props.resource} rowClick={null} data={data} total={1}>
        <ReferenceField source="customerId" reference="customers" label="Cliente" basePath="customers" sortable={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="attendantId" reference="attendants" label="Frentista" basePath="attendants" emptyText="Não identificado" sortable={false}>
          <FunctionField render={record => record && (record.name || record.identfid) ? record.name || record.identfid : 'Não identificado'} label='Nome/Identfid' emptyText="Não identificado" />
        </ReferenceField>
        <ReferenceField source="placeId" reference="stations" label="Posto" basePath="stations" sortable={false}>
          <TextField source="name" />
        </ReferenceField>
        <CheckChainUsesModule module={ModuleKey.PROMOTER} label="Promoter" basePath="promoters" sortable={false}>
          <ReferenceField source="promoterId" reference="promoters" emptyText="Não definido">
            <TextField source="name" />
          </ReferenceField>
        </CheckChainUsesModule>
        <DateField source="receivedAt" label="Data do Abastecimento" sortable={false} showTime locales="pt-BR" />
      </Datagrid>
    </>
  );
}

const FilterFirstFillinsByAttendant = (props) => {
  const employeeType = getEmployeeType();
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const { filterValues } = useListContext();

  return (
    <Filter {...props}>
      <BetweenDatesInput alwaysOn options={{ disableFuture: true }} />
      <ReferenceInput
        source="placeId"
        reference="stations"
        sort={{ field: "name", order: "ASC" }}
        perPage={null}
        filter={permissions === PERMISSIONS.ADMIN ? {} : { chainId: getChainId(), type: 'station' }}
        alwaysOn
      >
        <AutocompleteInput
          filterToQuery={filterToQuery}
          label="Posto"
          optionText="name"
          disabled={filterValues.tagIds}
          noOptionsText={translate('ra.input.autocomplete.no_options_text')}
        />
      </ReferenceInput>
      <ReferenceArrayInput
        source="attendantsIds"
        reference="attendants"
        perPage={null}
        sort={{ field: "name", order: "ASC" }}
        filter={{ chainId: getChainId() }}
        style={{ minWidth: 160 }}
        alwaysOn
      >
        <SelectArrayInput
          label="Frentistas"
          emptyText="Todos"
          optionText={record => formatAttendantLabel(record)}
        />
      </ReferenceArrayInput>
      <Permission permission={PERMISSIONS.TAG} alwaysOn>
        <ReferenceArrayInput
          source="tagIds"
          reference="chains/chainId/tag"
          perPage={null}
          sort={{ field: "name", order: "ASC" }}
          filter={{ type: TagType.station }}
          sx={refArrayInputSx}
        >
          <SelectArrayInput
            label="Etiquetas"
            optionText="name"
            disabled={filterValues.placeId}
          />
        </ReferenceArrayInput>
      </Permission>
      <SelectArrayInput
        label="Origem"
        source="origin"
        choices={FillinOrigin.fillinOriginChoices}
        style={{ minWidth: 160 }}
        alwaysOn
      />
      {
        (employeeType === EmployeeType.admin || [EmployeeType.chain, EmployeeType.place].includes(employeeType)) &&
        <CheckChainUsesModule module={ModuleKey.PROMOTER} alwaysOn>
          <ReferenceArrayInput
            source="promoterIds"
            reference="promoters"
            sort={{ field: "name", order: "ASC" }}
            perPage={null}
            filter={{ chainId: getChainId() }}
          >
            <SelectArrayInput
              emptyText="Todos"
              label="Promoter"
              style={{ minWidth: 180 }}
              helperText={false}
              optionText={'name'}
            />
          </ReferenceArrayInput>
        </CheckChainUsesModule>
      }
    </Filter>
  );
}

const FirstFillinByAttendantReport = (props) => {
  const { permissions, isLoading } = usePermissions();

  return (
    <>
      {!isLoading &&
        <List
          {...props}
          title="Primeiro Abastecimento por Frentista"
          basePath="reports"
          resource="chains/chainId/reports/first-fillin-by-attendant"
          sort={{ field: "createdAt", order: "DESC" }}
          filters={<FilterFirstFillinsByAttendant />}
          filter={permissions === PERMISSIONS.ADMIN ? {} : { chainId: getChainId() }}
          filterDefaultValues={{ from: moment().startOf('day').toISOString(), to: moment().endOf('day').toISOString() }}
          actions={<ListActions />}
        >
          <FirstFillinByAttendantList />
        </List>
      }
    </>
  );
}

export default FirstFillinByAttendantReport;
